import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface ConfiguracaoIA {
  nome_assistente: string;
  mensagem_boas_vindas_publica: string;
  mensagem_boas_vindas_interna: string;
  horario_funcionamento: string;
  formas_atuacao: string;
  conteudo_site_publico: string;
  contato_publico: string;
  topicos_publicos_liberados: string[];
  perfis_internos_habilitados: string[];
  audio_entrada_publico: boolean;
  audio_saida_publico: boolean;
  audio_entrada_interno: boolean;
  audio_saida_interno: boolean;
  voz_publica: string;
  voz_interna: string;
  instrucoes_publicas: string;
  tom_publico: 'acolhedor' | 'caloroso' | 'orientador';
  perfil_conversa_interna: 'parceria_pedagogica' | 'conselheira' | 'objetiva_porem_gentil';
  instrucoes_internas: string;
  // Provedor de IA por creche (cada creche traz a própria chave)
  provedor_ia?: 'gemini' | 'openai' | 'claude' | 'groq' | '';
  modelo_ia?: string;
  api_key_ia_configurada?: boolean; // read-only vindo do backend (nunca a chave crua)
  // Avatar da assistente da creche (URL ou data URI). O avatar da Liz é do SaaS.
  avatar_url?: string;
}

const PROVEDORES_IA: Array<{ value: 'gemini' | 'openai' | 'claude' | 'groq'; label: string; exemploChave: string }> = [
  { value: 'gemini', label: 'Google Gemini', exemploChave: 'AIza...' },
  { value: 'openai', label: 'OpenAI (GPT)', exemploChave: 'sk-...' },
  { value: 'claude', label: 'Anthropic (Claude)', exemploChave: 'sk-ant-...' },
  { value: 'groq', label: 'Groq', exemploChave: 'gsk_...' },
];

const CONFIG_INICIAL: ConfiguracaoIA = {
  nome_assistente: 'Assistente da Creche',
  mensagem_boas_vindas_publica: 'Olá! Sou a assistente virtual da nossa creche. Estou aqui para cuidar de cada dúvida sua com todo o carinho. Como posso apoiar você e sua família hoje?',
  mensagem_boas_vindas_interna: 'Oi equipe! Sou a assistente virtual da creche. Estou pronta para ajudar na nossa rotina e cuidar do que precisarem. Podem contar comigo!',
  horario_funcionamento: '',
  formas_atuacao: '',
  conteudo_site_publico: '',
  contato_publico: '',
  topicos_publicos_liberados: ['acolhimento', 'horarios', 'cardapio', 'duvidas_pedagogicas', 'contato'],
  perfis_internos_habilitados: ['gestor', 'admin', 'professor', 'administrativo'],
  audio_entrada_publico: true,
  audio_saida_publico: true,
  audio_entrada_interno: true,
  audio_saida_interno: true,
  voz_publica: '',
  voz_interna: '',
  instrucoes_publicas: 'Seja sempre empática, demonstre que entende o coração de uma mãe/pai preocupados. Nunca pareça um sistema robotizado.',
  tom_publico: 'acolhedor',
  perfil_conversa_interna: 'parceria_pedagogica',
  instrucoes_internas: 'Apoie os professores como uma colega experiente. Entenda os desafios emocionais da creche e ofereça sempre conselhos gentis e construtivos.',
  provedor_ia: '',
  modelo_ia: '',
  api_key_ia_configurada: false,
  avatar_url: '',
};

export default function IAConfiguracaoPanel() {
  const [config, setConfig] = useState<ConfiguracaoIA>(CONFIG_INICIAL);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  // Campo da chave é write-only: o backend nunca devolve a chave crua, só o
  // status `api_key_ia_configurada`. Só enviamos a chave quando o gestor digita.
  const [novaChave, setNovaChave] = useState('');

  async function carregar() {
    try {
      const { data } = await api.get<Partial<ConfiguracaoIA>>('/ia/configuracao');
      setConfig({ ...CONFIG_INICIAL, ...data });
    } catch {
      setMensagem('Não foi possível carregar a configuração da assistente.');
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  async function salvar() {
    setSalvando(true);
    try {
      const payload: any = { ...config };
      delete payload.api_key_ia_configurada; // read-only, nunca enviar
      if (novaChave.trim()) payload.api_key_ia = novaChave.trim(); // só envia se digitou
      await api.put('/ia/configuracao', payload);
      setNovaChave('');
      await carregar(); // recarrega status "configurada" a partir do backend
      setMensagem('Configurações da assistente salvas com sucesso!');
    } catch {
      setMensagem('Ops, não consegui salvar as configurações da assistente. Tente novamente!');
    } finally {
      setSalvando(false);
    }
  }

  async function removerChave() {
    if (!window.confirm('Remover a chave de IA desta creche? A assistente ficará desligada até você configurar uma nova chave.')) return;
    setSalvando(true);
    try {
      const payload: any = { ...config, api_key_ia: '' };
      delete payload.api_key_ia_configurada;
      await api.put('/ia/configuracao', payload);
      setNovaChave('');
      await carregar();
      setMensagem('Chave de IA removida. A assistente desta creche está desligada.');
    } catch {
      setMensagem('Não consegui remover a chave agora. Tente novamente!');
    } finally {
      setSalvando(false);
    }
  }

  function onAvatarSelecionado(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMensagem('O avatar precisa ser uma imagem (PNG, JPG ou WEBP).');
      return;
    }
    if (file.size > 500 * 1024) {
      setMensagem('Imagem muito grande. Use um avatar de até 500 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setConfig((prev) => ({ ...prev, avatar_url: String(reader.result || '') }));
      setMensagem('Avatar carregado. Clique em "Oficializar" para salvar.');
    };
    reader.readAsDataURL(file);
  }

  return (
    <section className="ia-box">
      <div className="ia-section-header">
        <div>
          <h3>Certidão de Nascimento: O Batismo da sua Assistente</h3>
          <p className="texto-ajuda">
            Cada creche tem a <strong>sua própria assistente exclusiva</strong> — com nome, personalidade e chave de IA próprios.
            (A <strong>Liz</strong> é a assistente comercial do SaaS Gestão Creches; ela ajuda na contratação, mas <strong>não</strong> é a assistente da sua creche.)
            Que tal envolver os alunos numa votação para batizar a assistente de vocês? Isso cria um vínculo mágico entre a tecnologia e o dia a dia escolar.
          </p>
        </div>
        <button className="btn-primario" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Oficializar Batismo e Essência'}
        </button>
      </div>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}

      <div className="form-grid ia-config-grid">
        <label>Nome Oficial da Assistente (Batismo Local)
          <input
            placeholder="Ex: Estrela, Tia Maria, Amiguinha..."
            value={config.nome_assistente}
            onChange={(e) => setConfig((prev) => ({ ...prev, nome_assistente: e.target.value }))}
          />
        </label>
        <label>Avatar da Assistente (foto/imagem própria da creche)
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            <img src={config.avatar_url || '/assistente_avatar.svg'} alt="Avatar da assistente" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '2px solid #ff7fa4' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onAvatarSelecionado} />
              {config.avatar_url && (
                <button type="button" className="btn-secundario" style={{ width: 'fit-content' }} onClick={() => setConfig((prev) => ({ ...prev, avatar_url: '' }))}>
                  Remover avatar
                </button>
              )}
            </div>
          </div>
          <small>Cada creche usa a imagem da sua própria assistente (até 500 KB). O avatar da Liz é exclusivo do SaaS.</small>
        </label>
        <label>Horário de Atendimento e Disponibilidade
          <textarea rows={3} value={config.horario_funcionamento} onChange={(e) => setConfig((prev) => ({ ...prev, horario_funcionamento: e.target.value }))} />
        </label>
        <label>O 'Coração' da Assistente (Visão Pedagógica da Creche)
          <textarea 
            rows={3} 
            placeholder="Descreva aqui o que sua creche mais valoriza (Ex: Afetividade, Disciplina Positiva, Autonomia...)"
            value={config.formas_atuacao} 
            onChange={(e) => setConfig((prev) => ({ ...prev, formas_atuacao: e.target.value }))} 
          />
        </label>
        <label>Conhecimento Geral sobre a nossa Creche
          <textarea rows={4} value={config.conteudo_site_publico} onChange={(e) => setConfig((prev) => ({ ...prev, conteudo_site_publico: e.target.value }))} />
        </label>
        <label>Contatos Reais para Casos Críticos
          <textarea rows={3} value={config.contato_publico} onChange={(e) => setConfig((prev) => ({ ...prev, contato_publico: e.target.value }))} />
        </label>
        <label>Boas-vindas para as Famílias (Público)
          <textarea rows={3} value={config.mensagem_boas_vindas_publica} onChange={(e) => setConfig((prev) => ({ ...prev, mensagem_boas_vindas_publica: e.target.value }))} />
        </label>
        <label>Boas-vindas para a nossa Equipe (Interno)
          <textarea rows={3} value={config.mensagem_boas_vindas_interna} onChange={(e) => setConfig((prev) => ({ ...prev, mensagem_boas_vindas_interna: e.target.value }))} />
        </label>
        <label>Tópicos que ela pode responder aos pais
          <input value={config.topicos_publicos_liberados.join(', ')} onChange={(e) => setConfig((prev) => ({ ...prev, topicos_publicos_liberados: e.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} />
        </label>
        <label>Habilitar conversa interna para:
          <input value={config.perfis_internos_habilitados.join(', ')} onChange={(e) => setConfig((prev) => ({ ...prev, perfis_internos_habilitados: e.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} />
        </label>
        <label>Diretrizes de Empatia (Como ela deve agir?)
          <textarea rows={4} value={config.instrucoes_publicas} onChange={(e) => setConfig((prev) => ({ ...prev, instrucoes_publicas: e.target.value }))} placeholder="Ex: Sempre seja doce ao falar de medicação..." />
        </label>
        <label>Tom de Voz com as Famílias
          <select value={config.tom_publico} onChange={(e) => setConfig((prev) => ({ ...prev, tom_publico: e.target.value as ConfiguracaoIA['tom_publico'] }))}>
            <option value="acolhedor">Muito Acolhedor (Foco emocional total)</option>
            <option value="caloroso">Caloroso e Alegre (Energia positiva)</option>
            <option value="orientador">Orientador Gentil (Calma e clareza)</option>
          </select>
        </label>
        <label>Personalidade Interna (Equipe)
          <select value={config.perfil_conversa_interna} onChange={(e) => setConfig((prev) => ({ ...prev, perfil_conversa_interna: e.target.value as ConfiguracaoIA['perfil_conversa_interna'] }))}>
            <option value="parceria_pedagogica">Parceria Pedagógica (Foco no desenvolvimento)</option>
            <option value="conselheira">Conselheira Fraterna (Foco no apoio humano)</option>
            <option value="objetiva_porem_gentil">Profissional e Gentil (Foco em diretrizes)</option>
          </select>
        </label>
        <label>Voz da Assistente (Sotaque/Estilo)
          <input value={config.voz_publica} onChange={(e) => setConfig((prev) => ({ ...prev, voz_publica: e.target.value }))} placeholder="Ex.: Francisca (Voz suave)" />
        </label>
      </div>

      <div className="ia-checkbox-grid">
        <label><input type="checkbox" checked={config.audio_entrada_publico} onChange={(e) => setConfig((prev) => ({ ...prev, audio_entrada_publico: e.target.checked }))} /> Famílias podem enviar voz</label>
        <label><input type="checkbox" checked={config.audio_saida_publico} onChange={(e) => setConfig((prev) => ({ ...prev, audio_saida_publico: e.target.checked }))} /> Assistente responde por áudio às famílias</label>
        <label><input type="checkbox" checked={config.audio_entrada_interno} onChange={(e) => setConfig((prev) => ({ ...prev, audio_entrada_interno: e.target.checked }))} /> Professores podem enviar voz</label>
        <label><input type="checkbox" checked={config.audio_saida_interno} onChange={(e) => setConfig((prev) => ({ ...prev, audio_saida_interno: e.target.checked }))} /> Assistente responde por áudio à equipe</label>
      </div>

      <div className="ia-divider" style={{ margin: '2rem 0', borderTop: '1px solid #eee' }} />

      <div className="ia-section-costs">
        <h3>Provedor de IA e Chave da Creche</h3>
        <p className="texto-ajuda" style={{ color: '#856404', backgroundColor: '#fff3cd', padding: '1rem', borderRadius: '8px', border: '1px solid #ffeeba', marginBottom: '1.5rem' }}>
          <strong>Importante:</strong> a assistente desta creche funciona com a <strong>chave de API da própria creche</strong>.
          Você escolhe o provedor (Gemini, GPT, Claude ou Groq) e cola a chave dele. Os custos de tokens são cobrados
          diretamente pelo provedor à creche, conforme o uso — são de responsabilidade exclusiva da creche.
          <br /><strong>Sem chave configurada, a assistente fica desligada.</strong>
        </p>

        <div className="ia-status-assistente" style={{
          padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: 600,
          background: config.api_key_ia_configurada ? '#e8f5e9' : '#fdecea',
          color: config.api_key_ia_configurada ? '#2e7d32' : '#c62828',
          border: `1px solid ${config.api_key_ia_configurada ? '#a5d6a7' : '#f5c6cb'}`,
        }}>
          {config.api_key_ia_configurada
            ? `✅ Assistente ATIVA — chave configurada${config.provedor_ia ? ` (provedor: ${config.provedor_ia})` : ''}.`
            : '⛔ Assistente DESLIGADA — configure o provedor e a chave abaixo para ativá-la.'}
        </div>

        <div className="form-grid">
          <label>Provedor de IA
            <select
              value={config.provedor_ia || ''}
              onChange={(e) => setConfig((prev) => ({ ...prev, provedor_ia: e.target.value as ConfiguracaoIA['provedor_ia'] }))}
            >
              <option value="">Selecione o provedor...</option>
              {PROVEDORES_IA.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <label>Modelo (opcional)
            <input
              placeholder="Deixe vazio para usar o modelo padrão do provedor"
              value={config.modelo_ia || ''}
              onChange={(e) => setConfig((prev) => ({ ...prev, modelo_ia: e.target.value }))}
            />
          </label>
          <label>Chave de API da Creche {config.api_key_ia_configurada && <span style={{ color: '#2e7d32', fontWeight: 600 }}>• já configurada</span>}
            <input
              type="password"
              autoComplete="new-password"
              placeholder={config.api_key_ia_configurada
                ? 'Chave já salva — digite uma nova só para substituir'
                : `Cole aqui a chave (ex.: ${PROVEDORES_IA.find((p) => p.value === config.provedor_ia)?.exemploChave || 'sua chave...'})`}
              value={novaChave}
              onChange={(e) => setNovaChave(e.target.value)}
            />
            <small>Nunca compartilhe sua chave. Ela é <strong>criptografada</strong> no servidor e nunca é exibida de volta — só usada nas chamadas da sua assistente.</small>
          </label>
          {config.api_key_ia_configurada && (
            <button type="button" className="btn-secundario" style={{ alignSelf: 'end' }} onClick={removerChave} disabled={salvando}>
              Remover chave (desligar assistente)
            </button>
          )}
        </div>

        <div className="ia-training-specs" style={{ marginTop: '2rem' }}>
          <h4>Formatos Suportados para Treinamento</h4>
          <ul style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', padding: '0', listStyle: 'none' }}>
            <li>📄 <strong>PDF:</strong> Documentos pedagógicos e regimentos.</li>
            <li>📝 <strong>Word (.docx):</strong> Planejamentos e projetos.</li>
            <li>📊 <strong>Excel (.xlsx):</strong> Cardápios e cronogramas.</li>
            <li>📹 <strong>Vídeos (MP4):</strong> Treinamentos da equipe (máx. 100MB).</li>
            <li>🔗 <strong>Links:</strong> Artigos e referências externas.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
